'use client';

import type { MouseEvent, ReactNode } from 'react';
import Link from 'next/link';
import * as Sentry from 'utils/errorTracking';
import { styled } from '@mui/material/styles';
import { trackEventWithGTM } from 'utils/analytics';
import { COLORS, FONTS } from 'components/HomepageV3/theme/tokens';
import EditorialButton from 'components/HomepageV3/components/EditorialButton';
import { withSe3AssetPrefix } from 'components/HomepageV3/assetPrefix';
import {
  SPOTLIGHT_OWN8,
  SPOTLIGHT_DISRUPTORS,
  type Spotlight,
} from 'components/HomepageV3/data/demoData';

/**
 * OfferingSpotlight — `.se-spotlight.se-spotlight-split` featured-deal band.
 *
 * A dark night-surface band with a radial-gradient background. Split layout:
 *  - left: a visual panel — either the OWN8 "StartEngine" revenue chart
 *    (`.se-own8-panel`) or a framed campaign image (`.se-spotlight-imgwrap`);
 *  - right (`.se-spotlight-left`): logo / eyebrow label + mono series chip,
 *    serif name, description (with optional emphasised fragments), the CTA
 *    (`EditorialButton`) and a disclosure block.
 *
 * Source: design-refs/source/assets/private-home.css (`.se-spotlight*`,
 * `.se-own8-*`) + private-home.js `featuredBlock()` (SE_FEATURED own8/default).
 */

export type OfferingSpotlightVariant = 'own8' | 'default';

export interface SpotlightChartBar {
  /** Serif value caption above the bar (e.g. "$23M"). */
  cap: string;
  /** Bar fill height as a percentage 0–100. */
  height: number;
  /** Mono year label beneath the bar (e.g. "'23"). */
  year: string;
  /** Peak (gold) bar — the OWN8 Owner's-Bonus accent treatment. */
  peak?: boolean;
}

export interface SpotlightDisclosure {
  /** Terms line (e.g. "$500 minimum · bonus shares on investments of $5,000+."). */
  terms?: ReactNode;
  /** Legal small-print line. */
  legal?: ReactNode;
}

export interface OfferingSpotlightProps {
  /** Featured variant. `own8` renders the StartEngine Owner's-Bonus panel. */
  variant?: OfferingSpotlightVariant;
  /** Featured offering data (defaults per variant). */
  spotlight?: Spotlight;
  /**
   * True while the Strapi placement / live offering that supplies `spotlight`
   * is still resolving. When set and no `spotlight` is available yet, the band
   * renders nothing instead of flashing the hardcoded demo default — so the
   * Strapi-configured offering (e.g. PsiQuantum) wins and stays rather than
   * briefly reverting to Disruptors Fund on hydration (QA).
   */
  isLoadingSpotlight?: boolean;
  /** OWN8 chart "Now Investing" pill label. */
  chartPill?: string;
  /** OWN8 chart head label / sub-label. */
  chartHead?: string;
  chartHeadSub?: string;
  /** OWN8 revenue chart bars. */
  chartBars?: SpotlightChartBar[];
  /** OWN8 chart footnote. */
  chartNote?: string;
  /** Disclosure block beneath the CTA. */
  disclosure?: SpotlightDisclosure;
  /**
   * Optional secondary control rendered beside the CTA (the handoff's
   * "View full offering →" link). When set, the CTA sizes to its content
   * instead of filling the row.
   */
  secondaryAction?: ReactNode;
  /** Optional campaign image for the default variant. */
  imageSrc?: string;
  imageAlt?: string;
  /**
   * On Explore, keep the campaign visual pinned beside the title block instead
   * of vertically centering it against variable-length disclosure copy.
   * Desktop only; mobile keeps its stacked layout.
   */
  alignImageWithTitle?: boolean;
  /** CTA click handler. Called after analytics tracking fires. */
  onCtaClick?: (event: MouseEvent<HTMLElement>) => void;
  /**
   * Campaign-image click handler. Defaults to the CTA path, which is right
   * wherever the CTA opens the offering. Surfaces whose CTA does something
   * else (e.g. the Canada spotlight's "Book a Call") pass their own navigation
   * here so the image still behaves like a link to the offering, and own their
   * analytics for it.
   */
  onImageClick?: (event: MouseEvent<HTMLElement>) => void;
  trackingPage?: 'homepage_v3' | 'explore';
  trackingSurface?: 'homepage_spotlight' | 'explore_spotlight';
  className?: string;
  'data-cy'?: string;
}

const DEFAULT_CHART_PILL = 'Now Investing';
const DEFAULT_CHART_HEAD = 'Revenue';
const DEFAULT_CHART_HEAD_SUB = 'FY23 — FY25';
const DEFAULT_CHART_NOTE = '≈2× growth, two years running';

const DEFAULT_CHART_BARS: SpotlightChartBar[] = [
  { cap: '$23M', height: 21, year: "'23" },
  { cap: '$48M', height: 44, year: "'24" },
  { cap: '$109M', height: 100, year: "'25", peak: true },
];

const DEFAULT_OWN8_DISCLOSURE: SpotlightDisclosure = {
  terms: (
    <>
      $500 minimum · bonus shares on investments of $5,000+.{' '}
      <Link href="/offering/startengine">Full details →</Link>
    </>
  ),
  legal: (
    <>
      This Reg A+ offering is made available through StartEngine, Inc. No broker-dealer or
      intermediary involved in offering. This investment is speculative, illiquid, and involves a
      high degree of risk, including the possible loss of your entire investment. For more
      information, please see the most recent{' '}
      <Link href="/offering/startengine">Offering Circular</Link> and{' '}
      <Link href="/offering/startengine">Risks</Link> related to this offering.
    </>
  ),
};

const DEFAULT_DISRUPTORS_DISCLOSURE: SpotlightDisclosure = {
  terms: (
    <>
      Available to accredited investors only. <Link href="/private">Full details →</Link>
    </>
  ),
  legal:
    'Membership interests in Series 4-1 of StartEngine Private Funds LLC — an indirect economic interest in the underlying companies through SPV ownership, not a direct share purchase. The underlying companies are not participating in or involved with this offering. This investment is speculative, illiquid, and involves a high degree of risk, including the possible loss of your entire investment.',
};

/* `.se-spotlight.se-spotlight-split` */
const Band = styled('section')({
  position: 'relative',
  overflow: 'hidden',
  background: COLORS.night,
  borderTop: '1px solid rgba(233,215,182,0.12)',
  borderBottom: `1px solid ${COLORS.gold}`,
  // PD-35999 — reserve the spotlight height so the demoData→live-offering swap
  // (which resolves ~2.9s after load) does not shift the page below it. The
  // reserve approximates the rendered split-layout height at each breakpoint.
  // Center the content vertically so the reserved-height slack is split evenly
  // top/bottom (otherwise the content sits at the top and the gap pools at the
  // bottom — uneven padding).
  minHeight: '498px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  '@media (max-width: 1080px)': {
    minHeight: '520px',
  },
  '@media (max-width: 640px)': {
    minHeight: 'auto',
  },
});

/* `.se-spotlight-bg` */
const Bg = styled('div')({
  position: 'absolute',
  inset: 0,
  background:
    'radial-gradient(130% 150% at 86% 12%, rgba(1,209,184,0.10), transparent 52%), linear-gradient(135deg, #0A2528 0%, #050F0F 70%)',
});

/* `.se-spotlight-content` (+ `.se-spotlight-split` override) */
const Content = styled('div', {
  shouldForwardProp: (prop) => prop !== 'alignImageWithTitle',
})<{ alignImageWithTitle?: boolean }>(({ alignImageWithTitle }) => ({
  position: 'relative',
  zIndex: 2,
  display: 'grid',
  gridTemplateColumns: '1.02fr 1fr',
  alignItems: 'center',
  gap: '52px',
  maxWidth: '1120px',
  margin: '0 auto',
  padding: '52px var(--pad)',
  ...(alignImageWithTitle
    ? {
        // The eyebrow occupies the space before the title. Pin the visual to
        // the title row so CMS-controlled legal-copy length cannot move it.
        '& .se-spotlight-imgwrap': {
          alignSelf: 'start',
          marginTop: '44px',
        },
      }
    : null),
  '@media (max-width: 1080px)': {
    gap: '28px',
  },
  '@media (max-width: 640px)': {
    gridTemplateColumns: '1fr',
    gap: '28px',
    alignItems: 'start',
    padding: '32px var(--pad)',
    ...(alignImageWithTitle
      ? {
          '& .se-spotlight-imgwrap': {
            alignSelf: 'auto',
            marginTop: 0,
          },
        }
      : null),
  },
}));

/* `.se-spotlight-imgwrap` */
const ImgWrap = styled('button')({
  border: '1px solid rgba(233,215,182,0.22)',
  borderRadius: '16px',
  overflow: 'hidden',
  background: '#050E14',
  boxShadow: '0 18px 44px rgba(0,0,0,0.32)',
  display: 'block',
  aspectRatio: '16 / 9',
  padding: 0,
  cursor: 'pointer',
});

/* `.se-spotlight-img` */
const Img = styled('img')({
  width: '100%',
  height: '100%',
  // Hannah QA: hero/spotlight images should fill the frame — no dead space.
  objectFit: 'cover',
  display: 'block',
});

const SpotlightLoadingPanel = styled('div')({
  aspectRatio: '16 / 9',
  borderRadius: '16px',
  background:
    'linear-gradient(100deg, rgba(233,215,182,0.07) 20%, rgba(233,215,182,0.16) 38%, rgba(233,215,182,0.07) 56%)',
  backgroundSize: '220% 100%',
  animation: 'se3-spotlight-shimmer 1.5s ease-in-out infinite',
  '@keyframes se3-spotlight-shimmer': {
    '0%': { backgroundPosition: '100% 0' },
    '100%': { backgroundPosition: '-120% 0' },
  },
});

const SpotlightLoadingCopy = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: '16px',
});

const SpotlightLoadingLine = styled('div', {
  shouldForwardProp: (prop) => prop !== 'width',
})<{ width: string }>(({ width }) => ({
  width,
  height: '14px',
  borderRadius: '999px',
  background: 'rgba(233,215,182,0.14)',
  '&.heading': { height: '52px', borderRadius: '6px' },
  '&.cta': { height: '54px', width: '100%', marginTop: '8px' },
  '&.legal': { height: '10px', marginTop: '4px' },
}));

/* `.se-spotlight-imgwrap.se-own8-panel` */
const Own8Panel = styled('div')({
  containerType: 'inline-size',
  aspectRatio: '1200 / 630',
  display: 'flex',
  flexDirection: 'column',
  padding: 'clamp(24px, 3.4cqw, 38px) clamp(26px, 3.6cqw, 42px) clamp(20px, 2.8cqw, 30px)',
  border: '1px solid rgba(233,215,182,0.32)',
  background: 'radial-gradient(125% 120% at 78% 0%, #18494E 0%, #103035 55%, #0B2125 100%)',
  boxShadow: 'inset 0 1px 0 rgba(233,215,182,0.12), 0 30px 70px rgba(0,0,0,0.5)',
  borderRadius: '16px',
  '@media (max-width: 640px)': {
    aspectRatio: 'auto',
    padding: '28px 24px',
  },
});

/* `.se-own8-top` */
const Own8Top = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '12px',
  marginBottom: 'auto',
});

/* `.se-own8-pill` */
const Own8Pill = styled('div')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  fontFamily: FONTS.sans,
  fontSize: 'clamp(8.5px, 1.1cqw, 10px)',
  fontWeight: 600,
  letterSpacing: '0.22em',
  textTransform: 'uppercase',
  color: COLORS.champagne,
  background: 'rgba(233,215,182,0.1)',
  border: '1px solid rgba(233,215,182,0.2)',
  padding: '6px 13px',
  borderRadius: '999px',
  '& .dot': {
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    background: COLORS.teal,
    display: 'block',
  },
});

/* `.se-own8-chart-head` */
const Own8ChartHead = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
  gap: '3px',
  textAlign: 'right',
  fontFamily: FONTS.sans,
  fontSize: 'clamp(9px, 1.2cqw, 11px)',
  fontWeight: 600,
  letterSpacing: '0.18em',
  textTransform: 'uppercase',
  color: COLORS.champagne,
  '& span': {
    fontFamily: FONTS.mono,
    fontSize: 'clamp(8px, 1cqw, 9.5px)',
    letterSpacing: '0.06em',
    color: 'rgba(233,215,182,0.55)',
  },
});

/* `.se-own8-chart` */
const Own8Chart = styled('div')({
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'space-between',
  gap: 'clamp(14px, 3cqw, 32px)',
  height: 'clamp(220px, 40cqw, 380px)',
  margin: 'clamp(14px, 2.4cqw, 22px) 0 10px',
  padding: '0 2px',
  borderBottom: '1px solid rgba(233,215,182,0.2)',
  '@media (max-width: 640px)': {
    height: '180px',
    margin: '14px 0 10px',
  },
});

function barBackground(peak?: boolean, second?: boolean): string {
  if (peak) return 'linear-gradient(180deg, #E6B23E, #C8922A)';
  return second ? COLORS.tealInk : '#1C5A55';
}

/* `.se-own8-col` (+ `.is-peak` — the Owner's-Bonus gold accent) */
const Own8Col = styled('div', {
  shouldForwardProp: (prop) => prop !== 'peak' && prop !== 'second',
})<{ peak?: boolean; second?: boolean }>(({ peak, second }) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'flex-end',
  height: '100%',
  gap: '7px',
  '& .cap': {
    fontFamily: FONTS.serif,
    fontSize: 'clamp(12px, 1.9cqw, 17px)',
    color: peak ? '#fff' : COLORS.champagne,
    fontWeight: peak ? 500 : 400,
    lineHeight: 1,
  },
  '& i': {
    display: 'block',
    width: '100%',
    maxWidth: '56px',
    borderRadius: '6px 6px 0 0',
    background: barBackground(peak, second),
    ...(peak
      ? { boxShadow: '0 0 0 1px rgba(230,178,62,0.45), 0 -6px 22px rgba(230,178,62,0.3)' }
      : null),
  },
  '& .yr': {
    fontFamily: FONTS.mono,
    fontSize: 'clamp(8px, 1cqw, 10px)',
    fontWeight: 600,
    letterSpacing: '0.1em',
    color: peak ? '#E6B23E' : 'rgba(255,255,255,0.5)',
  },
}));

/* `.se-own8-chart-note` */
const Own8ChartNote = styled('div')({
  fontFamily: FONTS.mono,
  fontSize: 'clamp(8.5px, 1.05cqw, 10px)',
  fontWeight: 600,
  letterSpacing: '0.06em',
  color: COLORS.teal,
  textAlign: 'center',
});

/* `.se-spotlight-left` (+ `.se-spotlight-split` override) */
const Left = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  gap: '14px',
});

/* `.se-spotlight-eyebrow` */
const Eyebrow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '14px',
  flexWrap: 'wrap',
});

/* `.se-spotlight-lbl` */
const Lbl = styled('span')({
  fontFamily: FONTS.sans,
  fontSize: '11px',
  fontWeight: 600,
  letterSpacing: '0.32em',
  textTransform: 'uppercase',
  color: 'rgba(233,215,182,0.75)',
});

/* `.se-spotlight-series` */
const Series = styled('span')({
  fontFamily: FONTS.mono,
  fontSize: '11px',
  color: COLORS.champagne,
  background: 'rgba(233,215,182,0.08)',
  border: '1px solid rgba(233,215,182,0.25)',
  padding: '4px 10px',
  borderRadius: '4px',
  width: 'fit-content',
  letterSpacing: '0.04em',
  whiteSpace: 'nowrap',
});

/* `.se-spotlight-name` (+ `.se-spotlight-split` 44px override) */
const Name = styled('div')({
  fontFamily: FONTS.serif,
  fontWeight: 400,
  fontSize: '44px',
  lineHeight: 1,
  color: '#fff',
  letterSpacing: '-0.01em',
});

/* `.se-spotlight-desc` (+ `.se-spotlight-split` 16px override) */
const Desc = styled('div')({
  fontFamily: FONTS.sans,
  fontSize: '16px',
  color: COLORS.onDark2,
  lineHeight: 1.6,
  maxWidth: '480px',
  '& strong': { color: '#fff', fontWeight: 600 },
});

/* `.se-spotlight-actions` */
const Actions = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '20px',
  marginTop: '8px',
});

const SpotlightCta = styled(EditorialButton, {
  shouldForwardProp: (prop) => prop !== 'hasSecondaryAction',
})<{ hasSecondaryAction?: boolean }>(({ hasSecondaryAction }) => ({
  width: hasSecondaryAction ? 'auto' : '100%',
  fontSize: '12px',
  letterSpacing: '0.16em',
  padding: hasSecondaryAction ? '15px 30px' : '15px 28px',
}));

/* `.se-spotlight-disc` (split variant) */
const Disc = styled('div')({
  gridColumn: '1 / -1',
  fontFamily: FONTS.sans,
  fontSize: '13px',
  color: COLORS.onDark3,
  lineHeight: 1.5,
  margin: '12px 0 0',
  '& a': { color: 'rgba(233,215,182,0.7)', textDecoration: 'none' },
});

/* `.se-disc-terms` */
const DiscTerms = styled('span')({
  display: 'block',
});

/* `.se-disc-legal` */
const DiscLegal = styled('span')({
  display: 'block',
  marginTop: '10px',
  paddingTop: '10px',
  borderTop: '1px solid rgba(233,215,182,0.14)',
  fontSize: '11px',
  lineHeight: 1.55,
  color: 'rgba(233,215,182,0.45)',
});

/**
 * Wraps any plain-text emphasis fragments found in `desc` in <strong>, so a
 * data-driven `Spotlight` (which stores emphasis as plain strings) renders the
 * same bold treatment the source markup hard-codes via <strong>.
 */
function renderDescription(desc: string, emphasis?: string[]): ReactNode {
  if (!emphasis || emphasis.length === 0) return desc;
  const fragment = emphasis.find((e) => desc.includes(e));
  if (!fragment) return desc;
  const idx = desc.indexOf(fragment);
  return (
    <>
      {desc.slice(0, idx)}
      <strong>{fragment}</strong>
      {desc.slice(idx + fragment.length)}
    </>
  );
}

function resolveSpotlightImage(src?: string): string | undefined {
  if (!src) return undefined;
  if (/^(?:[a-z][a-z\d+\-.]*:)?\/\//i.test(src) || src.startsWith('data:')) return src;
  if (src.startsWith('/')) return withSe3AssetPrefix(src);
  if (src.startsWith('assets/')) {
    return withSe3AssetPrefix(`/homepage-v3/${src.slice('assets/'.length)}`);
  }
  return withSe3AssetPrefix(`/homepage-v3/${src}`);
}

function SpotlightLoadingState({ dataCy, className }: { dataCy: string; className?: string }) {
  return (
    <Band
      className={`se-spotlight se-spotlight-split ${className ?? ''}`}
      data-cy={dataCy}
      aria-busy="true"
      aria-label="Loading featured offering"
    >
      <Bg className="se-spotlight-bg" />
      <Content className="se-spotlight-content">
        <SpotlightLoadingPanel data-cy="offering-spotlight-loading-image" />
        <SpotlightLoadingCopy data-cy="offering-spotlight-loading-copy">
          <SpotlightLoadingLine width="30%" />
          <SpotlightLoadingLine width="76%" className="heading" />
          <SpotlightLoadingLine width="92%" />
          <SpotlightLoadingLine width="68%" />
          <SpotlightLoadingLine width="100%" className="cta" />
          <SpotlightLoadingLine width="95%" className="legal" />
          <SpotlightLoadingLine width="82%" className="legal" />
        </SpotlightLoadingCopy>
      </Content>
    </Band>
  );
}

export function OfferingSpotlight({
  variant = 'own8',
  spotlight,
  isLoadingSpotlight = false,
  chartPill = DEFAULT_CHART_PILL,
  chartHead = DEFAULT_CHART_HEAD,
  chartHeadSub = DEFAULT_CHART_HEAD_SUB,
  chartBars = DEFAULT_CHART_BARS,
  chartNote = DEFAULT_CHART_NOTE,
  disclosure,
  secondaryAction,
  imageSrc,
  imageAlt,
  alignImageWithTitle = false,
  onCtaClick,
  onImageClick,
  trackingPage = 'homepage_v3',
  trackingSurface = 'homepage_spotlight',
  className,
  'data-cy': dataCy = 'offering-spotlight',
}: OfferingSpotlightProps) {
  const isOwn8 = variant === 'own8';
  // Don't flash the hardcoded demo default while the direct campaign record is
  // still loading. Keep the layout stable with an issuer-neutral skeleton.
  if (!spotlight && isLoadingSpotlight) {
    return <SpotlightLoadingState dataCy={dataCy} className={className} />;
  }
  const data = spotlight ?? (isOwn8 ? SPOTLIGHT_OWN8 : SPOTLIGHT_DISRUPTORS);
  const disc = disclosure ?? (isOwn8 ? DEFAULT_OWN8_DISCLOSURE : DEFAULT_DISRUPTORS_DISCLOSURE);
  const resolvedImage = resolveSpotlightImage(imageSrc ?? data.image);

  const handleCtaClick = (event: MouseEvent<HTMLElement>) => {
    try {
      trackEventWithGTM('homepage_v3_spotlight_cta_clicked', {
        page: trackingPage,
        surface: trackingSurface,
        variant,
        offering_name: data.name,
        series: data.series,
      });
    } catch (e) {
      Sentry.captureException(e, { tags: { area: 'homepage_v3' } });
    }
    onCtaClick?.(event);
  };

  const handleImageClick = onImageClick ?? handleCtaClick;

  return (
    <Band className={`se-spotlight se-spotlight-split ${className ?? ''}`} data-cy={dataCy}>
      <Bg className="se-spotlight-bg" />
      <Content
        className={`se-spotlight-content${alignImageWithTitle ? ' se-spotlight-align-image-title' : ''}`}
        alignImageWithTitle={alignImageWithTitle}
      >
        {isOwn8 ? (
          <Own8Panel
            className="se-spotlight-imgwrap se-own8-panel"
            data-cy="offering-spotlight-own8-panel"
          >
            <Own8Top className="se-own8-top">
              <Own8Pill className="se-own8-pill" data-cy="offering-spotlight-own8-pill">
                <span className="dot" />
                {chartPill}
              </Own8Pill>
              <Own8ChartHead className="se-own8-chart-head">
                {chartHead}
                <span>{chartHeadSub}</span>
              </Own8ChartHead>
            </Own8Top>
            <Own8Chart className="se-own8-chart" data-cy="offering-spotlight-own8-chart">
              {chartBars.map((bar, i) => (
                <Own8Col
                  key={bar.year}
                  className={`se-own8-col${bar.peak ? ' is-peak' : ''}`}
                  peak={bar.peak}
                  second={i === 1}
                  data-cy={bar.peak ? 'offering-spotlight-own8-peak' : undefined}
                >
                  <span className="cap">{bar.cap}</span>
                  <i style={{ height: `${bar.height}%` }} />
                  <span className="yr">{bar.year}</span>
                </Own8Col>
              ))}
            </Own8Chart>
            <Own8ChartNote className="se-own8-chart-note">{chartNote}</Own8ChartNote>
          </Own8Panel>
        ) : (
          resolvedImage && (
            <ImgWrap
              type="button"
              className="se-spotlight-imgwrap"
              onClick={handleImageClick}
              aria-label={`View ${data.name}`}
              data-cy="offering-spotlight-image-cta"
            >
              <Img
                className="se-spotlight-img"
                src={resolvedImage}
                alt={imageAlt ?? data.name}
                data-cy="offering-spotlight-image"
              />
            </ImgWrap>
          )
        )}

        <Left className="se-spotlight-left">
          <Eyebrow className="se-spotlight-eyebrow">
            <Lbl className="se-spotlight-lbl" data-cy="offering-spotlight-eyebrow">
              {data.eyebrowLabel}
            </Lbl>
            {data.series && (
              <Series className="se-spotlight-series" data-cy="offering-spotlight-series">
                {data.series}
              </Series>
            )}
          </Eyebrow>
          <Name className="se-spotlight-name" data-cy="offering-spotlight-name">
            {data.name}
          </Name>
          <Desc className="se-spotlight-desc" data-cy="offering-spotlight-desc">
            {renderDescription(data.desc, data.emphasis)}
          </Desc>
          <Actions className="se-spotlight-actions">
            <SpotlightCta
              variant="solid"
              hasSecondaryAction={Boolean(secondaryAction)}
              onClick={handleCtaClick}
              data-cy="offering-spotlight-cta"
            >
              {data.cta}
            </SpotlightCta>
            {secondaryAction}
          </Actions>
        </Left>
        {disc && (
          <Disc className="se-spotlight-disc" data-cy="offering-spotlight-disclosure">
            {disc.terms && <DiscTerms className="se-disc-terms">{disc.terms}</DiscTerms>}
            {disc.legal && <DiscLegal className="se-disc-legal">{disc.legal}</DiscLegal>}
          </Disc>
        )}
      </Content>
    </Band>
  );
}

export default OfferingSpotlight;
